import { PropsWithChildren, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { BrowserRouter } from 'react-router';
import { UserProfileProvider } from '../../context/UserProfileContext/UserProfileContext';
import { ContextsProvider } from '../../context/contextProviders';
import { OnlineStatusProvider } from '../../context/OnlineStatusContext/OnlineStatusContext';
import { RoutesContextProvider } from '../../context/routesContext';
import { SnackbarContextProvider } from '../../context/snackBar';
import { useEnv } from '../../context/EnvironmentContext/EnvironmentContext';
import { RoutesList } from '../RoutesList/RoutesList';
import { ComposeContexts } from '../ComposeProviders';
import { VERSION } from '../../utils/version';
import '../../styles/styles.scss';
import 'react-dates/initialize';
import { AuthProvider } from 'react-oidc-context';
import { UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';
import { AxiosInterceptor } from '../AxiosInterceptor/AxiosInterceptor';
import 'leaflet/dist/leaflet.css';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import L from 'leaflet';
import { FlagsProvider } from 'flagged';
import { FeatureFlag } from '../../constants/featureFlag';
import '../../styles/mobileStyles.scss';
import { UserImpersonationProvider } from '../../context/UserImpersonationContext';
import { QueryProvider } from '../../context/QueryProvider';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
// eslint-disable-next-line @nx/enforce-module-boundaries
import '../../../../../../styles/index.css';

L.Marker.prototype.options.icon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [24, 41],
  iconAnchor: [12, 41]
});

ModuleRegistry.registerModules([AllCommunityModule]);

export function Core({ children }: PropsWithChildren) {
  const [performAuth, setPerformAuth] = useState(false);

  const env = useEnv();

  const oidcConfig: UserManagerSettings = {
    authority: env.authUrl,
    client_id: env.authenticationClientId,
    redirect_uri: env.authRedirectUrl,
    post_logout_redirect_uri: env.authLogoutRedirectUrl,
    metadata: {
      issuer: `${env.authUrl}/`,
      authorization_endpoint: `${env.authUrl}/oauth2/authorize`,
      token_endpoint: `${env.authUrl}/oauth2/token`,
      end_session_endpoint: env.logoutUrl
    },
    userStore: new WebStorageStateStore({ store: window.localStorage })
  };

  const onSigninCallback = () =>
    window.history.replaceState({}, document.title, window.location.pathname);

  useEffect(() => {
    if (env?.sentryDsn) {
      Sentry.init({
        dsn: env.sentryDsn,
        release: VERSION,
        environment: env.environment,
        tracesSampleRate: 0.001,
        ignoreErrors: [
          'AbortError',
          'Network Error',
          /Request aborted/,
          /Refresh token is expired/,
          /Persisted access token data not found/
        ],
        beforeSend: (event, _) => {
          const requestURL = event.request?.url;
          // transifex is unreliable and throws a 403. Unknown cause.
          if (
            requestURL?.includes('transifex') ||
            requestURL?.includes(env.authUrl)
          ) {
            return null;
          }
          return event;
        }
      });
    }
    if (env?.baseUrl) {
      setPerformAuth(true);
    }
  }, [env]);

  return (
    // <Sentry.ErrorBoundary fallback={<ErrorPage />}>
    <FlagsProvider
      features={{
        [FeatureFlag.ReportingBulkApproval]:
          env.enableReportingBulkApproval ?? false,
        [FeatureFlag.FlaCpcNfrAdditionalApprovers]:
          env.enableFlaCpcNfrAdditionalApprovers ?? false,
        [FeatureFlag.PartnershipsOverviewPage]:
          env.enablePartnershipsOverviewPage ?? false,
        [FeatureFlag.FlaCpcNfrParallelSigning]:
          env.enableFlaCpcNfrParallelSigning ?? false,
        [FeatureFlag.ReportingQuality]: env.enableReportingQuality ?? false
      }}
    >
      <BrowserRouter basename={env.reactAppBaseRoute}>
        <SnackbarContextProvider hideProgressBar={true}>
          <QueryProvider>
            <OnlineStatusProvider>
              <AuthProvider {...oidcConfig} onSigninCallback={onSigninCallback}>
                <AxiosInterceptor>
                  <UserProfileProvider performAuth={performAuth}>
                    <UserImpersonationProvider>
                      <RoutesContextProvider>
                        <ContextsProvider>
                          <ComposeContexts>
                            {children}
                            <RoutesList />
                          </ComposeContexts>
                        </ContextsProvider>
                      </RoutesContextProvider>
                    </UserImpersonationProvider>
                  </UserProfileProvider>
                </AxiosInterceptor>
              </AuthProvider>
            </OnlineStatusProvider>
          </QueryProvider>
        </SnackbarContextProvider>
      </BrowserRouter>
    </FlagsProvider>
    // </Sentry.ErrorBoundary>
  );
}
